import { useState } from "react";

function notesStorageInfo(notes) {
  const bytes = new Blob([JSON.stringify(notes)]).size;
  const mb = bytes / (1024 * 1024);
  return { mb, pct: (mb / 5) * 100 };
}

export default function SavedNotesPanel({ notes, onDelete, onExport, onRename, onClearAll }) {
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [editingNameId, setEditingNameId] = useState(null);
  const [editingNameValue, setEditingNameValue] = useState("");

  const startEditName = (e, note) => {
    e.stopPropagation();
    setEditingNameId(note.id);
    setEditingNameValue(note.patientName || "");
  };

  const commitName = (id) => {
    onRename(id, editingNameValue.trim());
    setEditingNameId(null);
  };

  const filtered = notes.filter(n => {
    const q = search.toLowerCase();
    return !q || (n.patientName || "").toLowerCase().includes(q) || (n.dateTime || "").includes(q) || (n.visitType || "").toLowerCase().includes(q);
  });

  const copyNote = (n) => {
    const text = [
      n.patientName ? `Patient: ${n.patientName}` : null,
      `Date: ${n.dateTime}`,
      n.visitType ? `Visit: ${n.visitType}` : null,
      "", n.note,
      n.counseling ? `\nCOUNSELING SUMMARY:\n${n.counseling}` : null,
      n.treatmentPlan ? `\nTREATMENT PLAN:\n${n.treatmentPlan}` : null,
    ].filter(Boolean).join("\n");
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(n.id);
      setTimeout(() => setCopiedId(null), 2000);
    }).catch(() => {});
  };

  const printNote = (n) => {
    const w = window.open("", "_blank");
    w.document.write(`<!DOCTYPE html><html><head><title>Clinic Note — ${n.patientName || "Patient"}</title>
      <style>body{font-family:Georgia,serif;max-width:750px;margin:40px auto;color:#111;line-height:1.7}
      h1{font-size:20px;margin-bottom:4px}h2{font-size:13px;text-transform:uppercase;letter-spacing:1px;margin-top:28px;border-bottom:1px solid #ddd;padding-bottom:4px;color:#555}
      .meta{font-size:12px;color:#666;margin-bottom:24px}p{margin:6px 0;white-space:pre-wrap}
      .code{display:inline-block;padding:2px 10px;border-radius:20px;font-size:12px;margin:2px}
      .icd{background:#eff6ff;color:#1d4ed8}.cpt{background:#f0fdf4;color:#15803d}
      .footer{margin-top:40px;font-size:11px;color:#999;border-top:1px solid #ddd;padding-top:10px}</style>
      </head><body>
      <h1>Clinic Note</h1>
      <div class="meta">${n.patientName ? `Patient: <strong>${n.patientName}</strong> &nbsp;|&nbsp; ` : ""}Date: ${n.dateTime}${n.visitType ? ` &nbsp;|&nbsp; Visit: ${n.visitType}` : ""}</div>
      <h2>Clinic Note</h2><p>${(n.note || "").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br>")}</p>
      ${n.counseling ? `<h2>Counseling Summary</h2><p>${n.counseling.replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br>")}</p>` : ""}
      ${n.treatmentPlan ? `<h2>Treatment Plan</h2><p>${n.treatmentPlan.replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br>")}</p>` : ""}
      ${(n.icd10 || []).length ? `<h2>ICD-10 Codes</h2>${n.icd10.map(c => `<span class="code icd">${c.code}</span> ${c.description}<br>`).join("")}` : ""}
      ${(n.cpt || []).length ? `<h2>CPT Codes</h2>${n.cpt.map(c => `<span class="code cpt">${c.code}</span> ${c.description}<br>`).join("")}` : ""}
      <div class="footer">Generated by Epiphany MD &nbsp;·&nbsp; For documentation purposes only</div>
      </body></html>`);
    w.document.close(); w.print();
  };

  const previewText = (n) => {
    const lines = (n.note || "").split("\n").filter(l => l.trim());
    const preview = lines.slice(0, 2).join(" ").slice(0, 160);
    return preview + (lines.join(" ").length > 160 ? "…" : "");
  };

  const { mb, pct } = notesStorageInfo(notes);
  const storageColor = pct > 95 ? "#f87171" : pct > 80 ? "#fbbf24" : "rgba(255,255,255,0.3)";

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: "32px 40px 40px", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 10 }}>
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, color: "#f0ede8" }}>
          📋 Saved Notes <span style={{ fontSize: 14, color: "rgba(255,255,255,0.3)", fontFamily: "'Inter', sans-serif", fontWeight: 400 }}>({notes.length})</span>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, color: storageColor, background: "rgba(255,255,255,0.04)", border: `1px solid ${storageColor}40`, borderRadius: 20, padding: "3px 10px" }}>
            Storage: {mb.toFixed(2)}MB / 5MB
          </span>
          <button onClick={onExport} style={{ background: "rgba(52,211,153,0.1)", border: "1px solid rgba(52,211,153,0.25)", color: "#6ee7b7", borderRadius: 10, padding: "8px 14px", cursor: "pointer", fontSize: 12 }}>
            Export All (.txt)
          </button>
          {notes.length > 0 && (
            <button onClick={() => { onExport(); onClearAll(); }} style={{ background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.25)", color: "#f87171", borderRadius: 10, padding: "8px 14px", cursor: "pointer", fontSize: 12 }}>
              Export All & Clear
            </button>
          )}
        </div>
      </div>

      {pct > 95 && (
        <div style={{ background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.3)", borderRadius: 10, padding: "10px 16px", marginBottom: 14, fontSize: 13, color: "#f87171" }}>
          ⛔ Storage full — new notes cannot be saved. Export and clear old notes to continue.
        </div>
      )}
      {pct > 80 && pct <= 95 && (
        <div style={{ background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.3)", borderRadius: 10, padding: "10px 16px", marginBottom: 14, fontSize: 13, color: "#fbbf24" }}>
          ⚠️ Storage nearly full — export and clear old notes soon.
        </div>
      )}

      <input
        value={search} onChange={e => setSearch(e.target.value)}
        placeholder="Search by patient name or date…"
        style={{ width: "100%", padding: "10px 16px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, color: "#f0ede8", fontSize: 13, outline: "none", boxSizing: "border-box", marginBottom: 20 }}
      />

      {filtered.length === 0 && (
        <div style={{ textAlign: "center", padding: "48px 0", color: "rgba(255,255,255,0.3)", fontSize: 14 }}>
          {notes.length === 0 ? "No saved notes yet — generate a visit note to get started." : "No notes match your search."}
        </div>
      )}

      {filtered.map(note => {
        const expanded = expandedId === note.id;
        return (
          <div key={note.id} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14, marginBottom: 12, overflow: "hidden" }}>
            <div onClick={() => setExpandedId(expanded ? null : note.id)}
              style={{ padding: "16px 20px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}
              onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.02)"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4, flexWrap: "wrap" }}>
                  {editingNameId === note.id ? (
                    <input
                      autoFocus
                      value={editingNameValue}
                      onChange={e => setEditingNameValue(e.target.value)}
                      onBlur={() => commitName(note.id)}
                      onKeyDown={e => { if (e.key === "Enter") commitName(note.id); if (e.key === "Escape") setEditingNameId(null); }}
                      onClick={e => e.stopPropagation()}
                      style={{ fontFamily: "'Playfair Display', serif", fontSize: 15, fontWeight: 600, color: "#f0ede8", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(99,102,241,0.5)", borderRadius: 6, padding: "2px 10px", outline: "none", minWidth: 160 }}
                    />
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 15, fontWeight: 600, color: "#f0ede8" }}>
                        {note.patientName || <span style={{ color: "rgba(255,255,255,0.3)", fontStyle: "italic" }}>Unnamed Patient</span>}
                      </div>
                      <button onClick={e => startEditName(e, note)} title="Edit patient name"
                        style={{ background: "none", border: "none", color: "rgba(255,255,255,0.3)", cursor: "pointer", fontSize: 12, padding: "0 2px", lineHeight: 1, display: "flex", alignItems: "center" }}
                        onMouseEnter={e => e.currentTarget.style.color = "rgba(255,255,255,0.7)"}
                        onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.3)"}>
                        ✏️
                      </button>
                    </div>
                  )}
                  {note.visitType && <span style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", background: "rgba(255,255,255,0.05)", borderRadius: 20, padding: "2px 8px", flexShrink: 0 }}>{note.visitType}</span>}
                </div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", marginBottom: expanded ? 0 : 6 }}>🕐 {note.dateTime}</div>
                {!expanded && <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", lineHeight: 1.6, fontFamily: "Georgia, serif", fontStyle: "italic" }}>{previewText(note)}</div>}
              </div>
              <div style={{ color: "rgba(255,255,255,0.3)", fontSize: 14, marginLeft: 14, flexShrink: 0, marginTop: 3 }}>{expanded ? "▲" : "▼"}</div>
            </div>

            {expanded && (
              <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                <div style={{ display: "flex", gap: 8, padding: "12px 20px", borderBottom: "1px solid rgba(255,255,255,0.06)", flexWrap: "wrap" }}>
                  <button onClick={() => copyNote(note)} style={{ background: "rgba(99,102,241,0.2)", border: "1px solid rgba(99,102,241,0.4)", color: copiedId === note.id ? "#a5b4fc" : "#c7d2fe", borderRadius: 8, padding: "6px 14px", cursor: "pointer", fontSize: 12 }}>
                    {copiedId === note.id ? "✓ Copied!" : "📋 Copy"}
                  </button>
                  <button onClick={() => printNote(note)} style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.7)", borderRadius: 8, padding: "6px 14px", cursor: "pointer", fontSize: 12 }}>🖨️ Print</button>
                  {confirmDelete === note.id ? (
                    <>
                      <span style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", display: "flex", alignItems: "center" }}>Delete this note?</span>
                      <button onClick={() => { onDelete(note.id); setConfirmDelete(null); setExpandedId(null); }} style={{ background: "rgba(248,113,113,0.2)", border: "1px solid rgba(248,113,113,0.4)", color: "#f87171", borderRadius: 8, padding: "6px 14px", cursor: "pointer", fontSize: 12 }}>Yes, delete</button>
                      <button onClick={() => setConfirmDelete(null)} style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.4)", borderRadius: 8, padding: "6px 14px", cursor: "pointer", fontSize: 12 }}>Cancel</button>
                    </>
                  ) : (
                    <button onClick={() => setConfirmDelete(note.id)} style={{ background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.25)", color: "#f87171", borderRadius: 8, padding: "6px 14px", cursor: "pointer", fontSize: 12 }}>🗑️ Delete</button>
                  )}
                </div>
                <div style={{ padding: "16px 20px 20px" }}>
                  {[{ title: "Clinic Note", content: note.note }, { title: "Counseling Summary", content: note.counseling }, { title: "Treatment Plan", content: note.treatmentPlan }]
                    .filter(s => s.content)
                    .map(({ title, content }) => (
                      <div key={title} style={{ marginBottom: 18 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "1.2px", textTransform: "uppercase", color: "rgba(255,255,255,0.3)", marginBottom: 8 }}>{title}</div>
                        <div style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", lineHeight: 1.75, fontFamily: "Georgia, serif", whiteSpace: "pre-wrap" }}>{content}</div>
                      </div>
                    ))}
                  {((note.icd10 || []).length > 0 || (note.cpt || []).length > 0) && (
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
                      {(note.icd10 || []).map(c => <span key={c.code} style={{ fontSize: 11, background: "rgba(59,130,246,0.15)", border: "1px solid rgba(59,130,246,0.3)", color: "#93c5fd", borderRadius: 20, padding: "3px 10px" }}>{c.code} – {c.description}</span>)}
                      {(note.cpt || []).map(c => <span key={c.code} style={{ fontSize: 11, background: "rgba(16,185,129,0.15)", border: "1px solid rgba(16,185,129,0.3)", color: "#6ee7b7", borderRadius: 20, padding: "3px 10px" }}>{c.code} – {c.description}</span>)}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
